<script setup>
/**
 * Borderly custom: barra rápida de etapas del pipeline (p1…p7) debajo del
 * cuadro de redacción. Permite a los vendedores mover la conversación de
 * etapa sin abrir la barra lateral de etiquetas.
 *
 * Las etapas se descubren solas: cualquier etiqueta de la cuenta cuyo título
 * siga el patrón `p<N>_<nombre>` aparece aquí, ordenada por N. Si mañana se
 * agrega p8_algo, sale sin tocar este archivo.
 *
 * Las etapas son excluyentes: al elegir una se retira la anterior. Clic en la
 * etapa activa la quita.
 */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';

const PIPELINE_PATTERN = /^p(\d+)_/i;

const { accountLabels, activeLabels, onUpdateLabels } = useConversationLabels();

const currentChat = useMapGetter('getSelectedChat');
const uiFlags = useMapGetter('conversationLabels/getUIFlags');

const humanize = title =>
  title
    .replace(PIPELINE_PATTERN, '')
    .replace(/_/g, ' ')
    .replace(/^./, char => char.toUpperCase());

// Texto negro sobre colores claros, blanco sobre oscuros.
const readableTextColor = (hex = '') => {
  const value = hex.replace('#', '');
  if (value.length !== 6) return '#FFFFFF';
  const [r, g, b] = [0, 2, 4].map(i => parseInt(value.slice(i, i + 2), 16));
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1F2937' : '#FFFFFF';
};

const stages = computed(() =>
  accountLabels.value
    .filter(({ title }) => PIPELINE_PATTERN.test(title))
    .map(label => {
      const stage = Number(label.title.match(PIPELINE_PATTERN)[1]);
      return {
        ...label,
        stage,
        shortName: `P${stage}`,
        fullName: humanize(label.title),
      };
    })
    .sort((a, b) => a.stage - b.stage)
);

const activeTitles = computed(() => activeLabels.value.map(({ title }) => title));

const isActive = label => activeTitles.value.includes(label.title);

const isVisible = computed(() => !!currentChat.value?.id && stages.value.length > 0);

const onSelect = label => {
  if (uiFlags.value.isUpdating) return;
  // Se conservan las etiquetas que no son de pipeline (dormido, riesgo, usa…)
  const titles = activeTitles.value.filter(
    title => !PIPELINE_PATTERN.test(title)
  );
  if (!isActive(label)) titles.push(label.title);
  onUpdateLabels(titles);
};
</script>

<template>
  <div
    v-if="isVisible"
    class="flex items-center flex-wrap gap-1.5 px-4 py-1.5 border-t border-n-weak bg-n-surface-1"
  >
    <span class="text-xs font-medium text-n-slate-10 mr-0.5">Etapa</span>
    <button
      v-for="label in stages"
      :key="label.id"
      type="button"
      :title="`${label.shortName} · ${label.fullName}`"
      :disabled="uiFlags.isUpdating"
      class="flex items-center gap-1 h-6 px-2 rounded-full text-xs font-semibold border transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      :class="
        isActive(label)
          ? 'border-transparent'
          : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
      "
      :style="
        isActive(label)
          ? {
              backgroundColor: label.color,
              color: readableTextColor(label.color),
            }
          : {}
      "
      @click="onSelect(label)"
    >
      <span
        v-if="!isActive(label)"
        class="w-2 h-2 rounded-full shrink-0"
        :style="{ backgroundColor: label.color }"
      />
      <span>{{ label.shortName }}</span>
      <span v-if="isActive(label)" class="font-normal">
        {{ label.fullName }}
      </span>
    </button>
  </div>
</template>
